import { useQuery } from '@tanstack/react-query';
import { Song } from '@/types/player';
import { getAllSongs } from '@/services/songService';
import { cacheService, CACHE_KEYS } from '@/services/cacheService';
import { getDevicePerformance } from '@/config/performance';

export const useSongs = () => {
  const devicePerformance = getDevicePerformance();
  
  // TTL adaptativo baseado no device performance
  const getAdaptiveStaleTime = () => {
    const baseTTL = 5 * 60 * 1000; // 5 minutos base
    switch (devicePerformance) {
      case 'low':
        return baseTTL * 0.5; // Cache mais curto para dispositivos lentos
      case 'high':
        return baseTTL * 2; // Cache mais longo para dispositivos potentes
      default:
        return baseTTL;
    }
  };

  const query = useQuery({
    queryKey: ['songs'],
    queryFn: getAllSongs,
    staleTime: getAdaptiveStaleTime(),
    gcTime: devicePerformance === 'low' ? 5 * 60 * 1000 : 30 * 60 * 1000,
    retry: devicePerformance === 'low' ? 1 : 3,
    initialData: () => {
      // Tenta usar cache como dados iniciais (síncrono para React Query)
      const cached = cacheService.getSync<Song[]>(CACHE_KEYS.SONGS);
      return cached || undefined;
    }
  });

  const getSongById = (id: number): Song | undefined => {
    if (!query.data) return undefined;
    return query.data.find(song => song.id === id.toString());
  };

  return {
    data: query.data || null,
    isLoading: query.isLoading,
    error: query.error,
    getSongsByIds: async (ids: number[]): Promise<Song[]> => {
      if (!query.data) return [];
      return query.data.filter(song => ids.includes(Number(song.id)));
    },
    getSongById,
  };
}; 